import { array, arrayOf, bool, func, object, oneOf, shape, string } from 'prop-types';
import React, { useState, useEffect } from 'react';
import { ArrowLeft, ArrowRight, Calendar, MapPin, MessageSquare, Tag } from 'lucide-react';
import {
  BookingChevronRightIcon,
  BookingImageIcon,
  BookingInfoIcon,
  BookingTagIcon,
} from '../../components/OrderPanel/BookingPageIcons';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { compose } from 'redux';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';
import { convertMoneyToNumber } from '../../util/currency';
import {
  convertCategoriesList,
  ensureListing,
  ensureOwnListing,
  ensureUser,
  findDbCategoryByRef,
  getCategoryDisplayId,
  isLegacyNumericCategoryRef,
  transformDbCategories,
  userDisplayNameAsString,
} from '../../util/data';
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { richText } from '../../util/richText';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  DEAL_LISTING_TYPE,
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  PRODUCT_LISTING_TYPE,
  SERVICE_LISTING_TYPE,
  STAFF_LISTING_TYPE,
  propTypes,
} from '../../util/types';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  createSlug,
  parse,
} from '../../util/urlHelpers';

// Global ducks (for Redux actions and thunks)
import { getListingsById, getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';

// Shared components
import {
  BrandingIconCard,
  CurrencyAmount,
  H4,
  Heading,
  LayoutSingleColumn,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  Page,
} from '../../components';

// Related components and modules
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import {
  fetchDealItemMonthlyTimeSlots,
  fetchDealItemTimeSlots,
  fetchStaffListings,
  fetchTimeSlots,
  fetchTransactionLineItems,
  sendInquiry,
  setInitialValues,
  toggleCart,
} from './ListingPage.duck';

import ActionBarMaybe from './ActionBarMaybe';
import CustomListingFields from './CustomListingFields';
import {
  ErrorPage,
  LoadingPage,
  handleAddToCart,
  handleContactUser,
  handleSubmit,
  handleSubmitInquiry,
  listingImages,
  priceData,
} from './ListingPage.shared';
import SectionAuthorMaybe from './SectionAuthorMaybe';
import SectionTextMaybe from './SectionTextMaybe';

import { displayPrice } from '../../util/configHelpers.js';

import moment from 'moment';
import { classNames } from 'primereact/utils';
import Slider from 'react-slick';
import MerchantBanner from '../../components/MerchantBanner/MerchantBanner.js';
import ReviewCards from '../../components/ReviewCards/ReviewCards.js';
import ReviewSummary from '../../components/ReviewSummary/ReviewSummary.js';
import { createResourceLocatorString } from '../../util/routes.js';
import AlgoliaSearchDealCard from '../AlogoliaSearchCard/AlgoliaSearchDealCard.js';
import AlgoliaSearchServiceCard from '../AlogoliaSearchCard/AlgoliaSearchServiceCard.js';
import CategoryIcon from '../../components/CategoryIcon/CategoryIcon.js';
import { toggleFavorite } from '../WishlistPage/WishlistPage.duck.js';
import DealItemsMaybe from './DealItemsMaybe.js';
import ListingGallery from './ListingGallery/ListingGallery';
import css from './ListingPage.module.css';
// Analytics
import { trackEventsMixpanel } from '../../mixpanel/mixpanel';
import { Events, mixPanelEventsData, mixPanelEventsScreens } from '../../mixpanel/constants';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID, Money } = sdkTypes;

const listingTypeToSearchParam = listingType => {
  switch (listingType) {
    case DEAL_LISTING_TYPE:
      return 'deals';
    case SERVICE_LISTING_TYPE:
      return 'services';
    case PRODUCT_LISTING_TYPE:
      return 'products';
    default:
      return 'listings';
  }
};

// Maps a Sharetribe listing entity into the `hit` shape expected by the
// Algolia search cards, so the "Other Services/Deals" slider can reuse the
// same card UI used on the category pages. Images are reduced to URL strings
// because the Algolia cards render images via the `custom-image`/string path.
const listingToAlgoliaHit = listing => {
  const { id, attributes, author, images = [] } = listing || {};
  const { title, price, publicData } = attributes || {};
  const authorProfile = author?.attributes?.profile || {};

  const imageUrls = (images || [])
    .map(img => {
      const variants = img?.attributes?.variants || {};
      const variant =
        variants['listing-card-2x'] ||
        variants['listing-card'] ||
        Object.values(variants)[0];
      return variant?.url || null;
    })
    .filter(Boolean);

  return {
    objectID: id?.uuid,
    title,
    price: price ? { amount: price.amount, currency: price.currency } : null,
    images: imageUrls,
    publicData: publicData || {},
    author: {
      id: author?.id?.uuid,
      displayName: authorProfile.displayName,
      firstName: authorProfile.firstName,
      lastName: authorProfile.lastName,
      publicData: authorProfile.publicData || {},
    },
  };
};

const ListingSection = ({ icon, title, children, className }) => (
  <section className={classNames(css.sectionCard, className)}>
    <h2 className={css.sectionCardHeader}>
      <span className={css.sectionCardIconBadge}>{icon}</span>
      {title}
    </h2>
    {children}
  </section>
);

// Show Listing Price
const PriceMaybe = props => {
  const {
    price,
    publicData,
    validListingTypes,
    intl,
    marketplaceCurrency,
    showCurrencyMismatch = false,
  } = props;
  const { listingType, unitType } = publicData || {};

  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice || !price) {
    return null;
  }

  // Get formatted price or currency code if the currency does not match with marketplace currency
  const { formattedPrice, priceTitle } = priceData(price, marketplaceCurrency, intl);
  // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
  //       Instead, we show the currency code in place of the price
  return showCurrencyMismatch ? (
    <div className={css.priceContainerInCTA}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      <div className={css.perUnitInCTA}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </div>
    </div>
  ) : (
    <div className={css.priceContainer}>
      <span className={css.price}>
        <CurrencyAmount amount={price} symbolSize={18} />
      </span>
      {/* <span className={css.perUnit}>
        <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
      </span> */}
    </div>
  );
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(
    props.inquiryModalOpenForListingId === props.params.id
  );
  // const [activeTab, setActiveTab] = useState('About');

  // const handleTabClick = tabName => {
  //   setActiveTab(tabName);
  // };

  const [categoryVisible, setCategoryVisible] = useState([
    'category',
    'price',
    'tags',
    'otherInfo',
    'additionalInfo',
  ]);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingError,
    reviews,
    fetchReviewsInProgress,
    fetchReviewsError,
    ratings,
    fetchRatingsInProgress,
    fetchRatingsError,
    sendInquiryInProgress,
    sendInquiryError,
    monthlyTimeSlots,
    itemMonthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    onFetchStaffListings,
    staffListings,
    fetchStaffListingsError,
    fetchStaffListingsInProgress,
    onToggleCart,
    businessProfileListing,
    dealItemsListings,
    relatedItemsListings = [],
    onFetchDealItemMonthlyTimeSlots,
    onFetchDealItemTimeSlots,
    onToggleFavorite,
    categories = [],
  } = props;

  const queryParams = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const isBookingLink = queryParams?.bookingLink === true;

  const { currency: marketplaceCurrency } = config;

  const organizedCategories = transformDbCategories(categories) || [];
  const updatedDealCategories = convertCategoriesList(categories || []);

  const isDealItemClosed = dealItemsListings.reduce((acc, item) => {
    if (item?.attributes?.state !== 'published') {
      acc = true;
    }
    return acc;
  }, false);

  const toggleVisibility = name => {
    setCategoryVisible(prevState => {
      // Check if the category is already in the array
      if (prevState.includes(name)) {
        // If it's open (in the array), remove it
        return prevState.filter(item => item !== name);
      } else {
        // If it's closed (not in the array), add it
        return [...prevState, name];
      }
    });
  };
  const goBack = () => {
    history.goBack();
  };

  const userType = currentUser?.attributes?.profile?.publicData?.userType;
  const listingConfig = config.listing;
  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant = rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;
  const currentListing =
    isPendingApprovalVariant || isDraftVariant
      ? ensureOwnListing(getOwnListing(listingId))
      : ensureListing(getListing(listingId));

  const listingSlug = rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  const listingTab = isDraftVariant ? 'photos' : 'details';

  const isApproved =
    currentListing.id && currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage = pendingIsApproved || pendingOtherUsersListing;

  // Track: Customer visits a Deal listing (must be before any early returns)
  useEffect(() => {
    try {
      const isDealListing =
        currentListing?.attributes?.publicData?.listingType === DEAL_LISTING_TYPE;
      if (userType === 'customer' && isDealListing && currentListing?.id?.uuid) {
        trackEventsMixpanel(Events.CUSTOMER_REDEEM_DEAL, {
          action_type: mixPanelEventsData.DEAL_VIEWED,
          screen: mixPanelEventsScreens.LISTING_PAGE,
          user_type: 'customer',
          user_id: currentUser?.id?.uuid,
          deal_id: currentListing?.id?.uuid,
          deal_title: currentListing?.attributes?.title,
          merchant_id: currentListing?.author?.id?.uuid,
        });
      }
    } catch (e) {
      console.error('Deal view tracking failed', e);
    }
    // Fire once per page view keyed by listing id
  }, [currentListing?.id?.uuid, userType]);

  useEffect(() => {
    const isDealListing =
      currentListing?.attributes?.publicData?.listingType === DEAL_LISTING_TYPE;
    const dealServiceItem =
      isDealListing &&
      dealItemsListings?.[0]?.attributes?.publicData?.listingType === SERVICE_LISTING_TYPE
        ? dealItemsListings[0]
        : null;
    if (dealServiceItem) {
      onFetchDealItemMonthlyTimeSlots(dealServiceItem);
    }
  }, [
    currentListing?.id?.uuid,
    dealItemsListings?.[0]?.id?.uuid,
    onFetchDealItemMonthlyTimeSlots,
  ]);

  if (shouldShowPublicListingPage) {
    return <NamedRedirect name="ListingPage" params={params} search={location.search} />;
  }

  const topbar = <TopbarContainer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  } else if (!currentListing.id) {
    // Still loading the listing
    return <LoadingPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} />;
  }

  const {
    description = '',
    geolocation = null,
    price = new Money(0, marketplaceCurrency),
    title = '',
    publicData = {},
    metadata = {},
    state: listingState,
  } = currentListing.attributes;

  const isPendingApproval = listingState === LISTING_STATE_PENDING_APPROVAL;
  const isClosed = listingState === LISTING_STATE_CLOSED;
  const isDraft = listingState === LISTING_STATE_DRAFT;

  const { deal_start_date, deal_end_date } = publicData;

  const currentDate = moment();
  const dealStartDate = moment.unix(deal_start_date);
  const dealEndDate = moment.unix(deal_end_date);

  // Check if the deal is valid (start date <= current date <= end date)
  const isDealValid = currentDate.isBetween(dealStartDate, dealEndDate, null, '[]'); // '[]' includes start and end dates
  const isDealStarted = currentDate.isSameOrAfter(dealStartDate);

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable && currentListing.author.id.uuid === currentUser.id.uuid;

  const {
    listingType,
    transactionProcessAlias,
    unitType,
    categoryLevel1,
    categoryLevel2,
    original_price,
    otherInfo,
  } = publicData;

  // Resolved by slug — what listings store — with a uuid fallback. Matching on
  // id alone left this empty and the UI fell through to printing the raw slug.
  const categoryLevel1Object = findDbCategoryByRef(organizedCategories, categoryLevel1) || {};

  const categoryLevel2Object =
    findDbCategoryByRef(categoryLevel1Object?.childCategory, categoryLevel2) || {};

  // categories_optimization: resolve category ids to their canonical display id (follows mergedTo).
  const displayCategoryLevel1Id = getCategoryDisplayId(categoryLevel1, categories);
  const displayCategoryLevel2Id = getCategoryDisplayId(categoryLevel2, categories);

  /**
   * Human-readable name for a stored category reference. Three sources,
   * because three kinds of reference reach this page:
   *  - deals use their own hardcoded `dealCategory.*` translations
   *  - legacy numeric ids look up `category.{id}`
   *  - DB categories carry their own already-localised name, so they skip
   *    intl entirely — routing a slug through it yields `category.NaN`.
   */
  const categoryDisplayName = (ref, resolved, legacyDisplayId) => {
    if (!ref) return null;
    if (listingType === DEAL_LISTING_TYPE) {
      return intl.formatMessage({ id: `dealCategory.${ref}` });
    }
    if (isLegacyNumericCategoryRef(ref)) {
      return intl.formatMessage({
        id: `category.${legacyDisplayId}`,
        defaultMessage: resolved?.label || ref,
      });
    }
    return resolved?.label || ref;
  };

  const dealOriginalPrice = original_price
    ? new Money(original_price * 100, marketplaceCurrency)
    : null;
  const listingPrice = price || null;

  const isDeal = listingType === DEAL_LISTING_TYPE;
  const isStaff = listingType === STAFF_LISTING_TYPE;
  const dealItemPublicData = dealItemsListings?.[0]?.attributes?.publicData || {};

  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage topbar={topbar} scrollingDisabled={scrollingDisabled} intl={intl} invalidListing />
    );
  }
  const processName = resolveLatestProcessName(transactionProcessAlias.split('/')[0]);
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking ? ('booking' ? isPurchase : 'purchase') : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);

  const { publicData: userPublicData } = currentUser?.attributes?.profile || {};
  const { destinationId, businessId, isPayoutCreated } = userPublicData || {};

  const noPayoutDetailsSetWithOwnListing =
    isOwnListing && processType !== 'inquiry' && !(isPayoutCreated || destinationId);

  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage id="ListingPage.payoutDetailsWarning" values={{ processType }} />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });
  // Note: this is for inquiry state in booking and purchase processes. Inquiry process is handled through handleSubmit.
  const onSubmitInquiry = handleSubmitInquiry({
    ...commonParams,
    getListing,
    onSendInquiry,
    setInquiryModalOpen,
  });
  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed = currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const facebookImages = listingImages(currentListing, 'facebook');
  const twitterImages = listingImages(currentListing, 'twitter');
  const schemaImages = listingImages(
    currentListing,
    `${config.layout.listingImage.variantPrefix}-2x`
  ).map(img => img.url);
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const schemaPriceMaybe = price
    ? {
        price: intl.formatNumber(convertMoneyToNumber(price), {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
        priceCurrency: price.currency,
      }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability ? { availability: schemaAvailability } : {};

  // Cart functionality – add this within the component but before the return statement
  const onAddToCart = handleAddToCart({
    ...commonParams,
    location,
    currentUser,
    onToggleCart,
    listingId: listingId?.uuid,
    authorId: currentAuthor?.id?.uuid,
    listing: currentListing,
  });

  const cart = currentUser?.attributes?.profile?.privateData?.cart;
  const listingCartCount =
    (cart &&
      cart[currentAuthor?.id.uuid] &&
      cart[currentAuthor?.id.uuid][currentListing?.id?.uuid]?.count) ||
    0;

  const listingNotActive = isPendingApproval || isClosed || isDraft;

  const cartProps = {
    listing: currentListing,
    count: listingCartCount,
    incrementCart: onAddToCart,
    isListingPage: true,
    isOwnListing,
    listingNotActive,
  };
  // cart functionality ends
  const arabicLanguage =
    typeof window !== 'undefined' && localStorage.getItem('language') === 'Arabic';

  const getOtherLabel = listingType => {
    switch (listingType) {
      case PRODUCT_LISTING_TYPE:
        return <FormattedMessage id="ListingPage.otherProducts" />;
      case SERVICE_LISTING_TYPE:
        return <FormattedMessage id="ListingPage.otherServices" />;
      case DEAL_LISTING_TYPE:
        return <FormattedMessage id="ListingPage.otherDeals" />;
    }
  };

  function RelatedItemsNextArrow(props) {
    const { className, style, onClick } = props;
    return (
      <button
        type="button"
        className={classNames(className, css.relatedCarouselArrow)}
        style={style}
        onClick={onClick}
        aria-label="Next"
      >
        <ArrowRight size={18} strokeWidth={2.5} aria-hidden />
      </button>
    );
  }

  function RelatedItemsPrevArrow(props) {
    const { className, style, onClick } = props;
    return (
      <button
        type="button"
        className={classNames(className, css.relatedCarouselArrow)}
        style={style}
        onClick={onClick}
        aria-label="Previous"
      >
        <ArrowLeft size={18} strokeWidth={2.5} aria-hidden />
      </button>
    );
  }

  const settings = {
    dots: false,
    infinite: false,
    slidesToShow: 3,
    lazyLoad: true,
    slidesToScroll: 1,
    centerMode: false,
    speed: 500,
    nextArrow: <RelatedItemsNextArrow />,
    prevArrow: <RelatedItemsPrevArrow />,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 920,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 620,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          centerMode: false,
        },
      },
    ],
  };

  const { title: businessTitle, publicData: businessPublicData } = businessProfileListing || {};

  const { business_category, business_subcategory, location: businessLocation } =
    businessPublicData || {};

  const businessCategoryLevel1Object =
    findDbCategoryByRef(organizedCategories, business_category) || {};

  const businessCategoryLevel2Object =
    findDbCategoryByRef(businessCategoryLevel1Object?.childCategory, business_subcategory) || {};

  const merchantDetails = {
    name: businessTitle,
    location: businessLocation,
  };

  const searchListingType = listingTypeToSearchParam(listingType);

  const breadcrumbListLabel = (
    <FormattedMessage
      id={`ListingPage.breadcrumb${
        listingType === DEAL_LISTING_TYPE
          ? 'Deals'
          : listingType === SERVICE_LISTING_TYPE
          ? 'Services'
          : listingType === PRODUCT_LISTING_TYPE
          ? 'Products'
          : 'Listings'
      }`}
    />
  );

  const orderPanelCommonProps = {
    className: css.productOrderPanel,
    bookingPageLayout: true,
    // OrderPanel isn't Redux-connected -- it only knows the logged-in user if
    // a caller passes it. This call site never did, so anything inside
    // OrderPanel gating on currentUser (e.g. EmergencyServiceCta's
    // isAuthenticated check) silently saw a logged-out guest even when the
    // customer was authenticated.
    currentUser,
    isDealStarted,
    isDealValid,
    isOwnListing,
    onFetchStaffListings,
    staffListings,
    onSubmit: handleOrderSubmit,
    onSubmitInquiry,
    sendInquiryInProgress,
    sendInquiryError,
    fetchStaffListingsError,
    fetchStaffListingsInProgress,
    cartProps,
    authorLink: (
      <NamedLink
        className={css.authorNameLink}
        name="ListingPage"
        params={params}
        to={{ hash: '#author' }}
      >
        {authorDisplayName}
      </NamedLink>
    ),
    title: <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />,
    titleDesktop: (
      <H4 as="h1" className={css.orderPanelTitle}>
        <FormattedMessage id="ListingPage.orderTitle" values={{ title: richTitle }} />
      </H4>
    ),
    payoutDetailsWarning,
    author: ensuredAuthor,
    onManageDisableScrolling,
    onContactUser,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    validListingTypes: config.listing.listingTypes,
    marketplaceCurrency: config.currency,
    dayCountAvailableForBooking: config.stripe.dayCountAvailableForBooking,
    marketplaceName: config.marketplaceName,
    config,
    isBookingLink,
    merchantDetails,
    callSetInitialValues,
    onInitializeCardPaymentData,
    routeConfiguration,
    dealItemMonthlyTimeSlots: itemMonthlyTimeSlots,
    onFetchDealItemTimeSlots,
  };

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: description,
        name: schemaTitle,
        image: schemaImages,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={topbar} footer={<FooterContainer />}>
        <div className={classNames(css.listingWrapper, arabicLanguage && css.arabicDirection)}>
          {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
            <ActionBarMaybe
              className={css.actionBarForProductLayout}
              isOwnListing={isOwnListing}
              listing={currentListing}
              showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
              isDealItemClosed={isDealItemClosed || (isDeal && !dealItemsListings?.length)}
            />
          ) : null}
          {currentListing.id ? (
            <ActionBarMaybe
              className={css.actionBarForProductLayout}
              isOwnListing={isOwnListing}
              listing={currentListing}
              isDealItemClosed={isDealItemClosed || (isDeal && !dealItemsListings?.length)}
              editParams={{
                id: listingId.uuid,
                slug: listingSlug,
                type: listingPathParamType,
                tab: listingTab,
              }}
            />
          ) : null}
          <div className={css.bookingMain}>
            <nav className={css.breadcrumb} aria-label="Breadcrumb">
              <NamedLink className={css.breadcrumbLink} name="LandingPage">
                <FormattedMessage id="ListingPage.breadcrumbHome" />
              </NamedLink>
              <BookingChevronRightIcon className={css.breadcrumbSep} />
              <NamedLink
                className={css.breadcrumbLink}
                name="AlgoliaSearchPage"
                params={{ listingType: searchListingType }}
              >
                {breadcrumbListLabel}
              </NamedLink>
              <BookingChevronRightIcon className={css.breadcrumbSep} />
              <span className={css.breadcrumbCurrent}>
                <FormattedMessage id="ListingPage.breadcrumbBook" />
              </span>
            </nav>

            {isBookingLink ? (
              <MerchantBanner
                businessTitle={businessTitle}
                categoryLevel1={business_category}
                categoryLevel2={business_subcategory}
                categories={categories}
                intl={intl}
              />
            ) : null}

            <div className={css.bookingGrid}>
              {!(isOwnListing || isStaff) ? (
                <OrderPanel
                  {...orderPanelCommonProps}
                  listing={currentListing}
                  dealItemsListings={dealItemsListings}
                />
              ) : null}

              <div className={css.bookingExtras}>
                <ListingSection
                  icon={<BookingImageIcon />}
                  title={intl.formatMessage({ id: 'ListingPage.sectionGallery' })}
                  className={css.gallerySection}
                >
                  <ListingGallery
                    userType={userType}
                    listing={currentListing}
                    variantPrefix={config.layout.listingImage.variantPrefix}
                  />
                </ListingSection>

                {description ? (
                  <ListingSection
                    icon={<BookingInfoIcon />}
                    title={intl.formatMessage({ id: 'ListingPage.itemDescription' })}
                  >
                    <SectionTextMaybe
                      className={css.desktopDescription}
                      text={description}
                      showAsIngress
                    />
                  </ListingSection>
                ) : null}

                {categoryLevel1 || listingPrice || otherInfo ? (
                  <ListingSection
                    icon={<BookingTagIcon />}
                    title={intl.formatMessage({ id: 'ListingPage.sectionDetails' })}
                  >
                    <div className={css.accordionList}>
                      {categoryLevel1 ? (
                        <div className={css.accordionSections}>
                          <div
                            className={css.headingName}
                            onClick={() => toggleVisibility('category')}
                          >
                            <FormattedMessage id="ListingPage.category" />
                            <span className={css.arrow}>
                              {categoryVisible.includes('category') ? (
                                <BrandingIconCard type="uparrow" />
                              ) : (
                                <BrandingIconCard type="downarrow" />
                              )}
                            </span>
                          </div>
                          {categoryVisible.includes('category') ? (
                            <div className={css.categoryLevelList}>
                              {categoryLevel1 || categoryLevel2 ? (
                                <div className={css.categoryLevelRow}>
                                  <CategoryIcon
                                    category={categoryLevel2 ? categoryLevel2Object : categoryLevel1Object}
                                    parent={categoryLevel2 ? categoryLevel1Object : undefined}
                                    className={css.categoryLevelIcon}
                                  />
                                  <div className={css.categoryLevelName}>
                                    {categoryLevel1
                                      ? categoryDisplayName(
                                          categoryLevel1,
                                          categoryLevel1Object,
                                          displayCategoryLevel1Id
                                        )
                                      : null}
                                    {categoryLevel1 && categoryLevel2 ? (
                                      <span className={css.categoryLevelSep}>›</span>
                                    ) : null}
                                    {categoryLevel2
                                      ? categoryDisplayName(
                                          categoryLevel2,
                                          categoryLevel2Object,
                                          displayCategoryLevel2Id
                                        )
                                      : null}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {listingPrice ? (
                        <div className={css.accordionSections}>
                          <div
                            className={css.headingName}
                            onClick={() => toggleVisibility('price')}
                          >
                            <FormattedMessage id="ListingPage.itemPrice" />
                            <span className={css.arrow}>
                              {categoryVisible.includes('price') ? (
                                <BrandingIconCard type="uparrow" />
                              ) : (
                                <BrandingIconCard type="downarrow" />
                              )}
                            </span>
                          </div>
                          {categoryVisible.includes('price') ? (
                            <div
                              className={classNames(
                                css.priceContent,
                                dealOriginalPrice && css.dealDiscount
                              )}
                            >
                              {/* add a red lineStroke to this price if dealOriginalPrice exists */}
                              {dealOriginalPrice ? (
                                <PriceMaybe
                                  price={dealOriginalPrice}
                                  publicData={publicData}
                                  validListingTypes={config.listing.listingTypes}
                                  intl={intl}
                                  marketplaceCurrency={config.currency}
                                />
                              ) : null}

                              <PriceMaybe
                                price={listingPrice}
                                publicData={publicData}
                                validListingTypes={config.listing.listingTypes}
                                intl={intl}
                                marketplaceCurrency={config.currency}
                              />
                            </div>
                          ) : null}
                        </div>
                      ) : null}

                      {otherInfo ? (
                        <div className={css.accordionSections}>
                          <div
                            className={css.headingName}
                            onClick={() => toggleVisibility('otherInfo')}
                          >
                            <FormattedMessage id="ListingPage.otherInformation" />
                            <span className={css.arrow}>
                              {categoryVisible.includes('otherInfo') ? (
                                <BrandingIconCard type="uparrow" />
                              ) : (
                                <BrandingIconCard type="downarrow" />
                              )}
                            </span>
                          </div>
                          {categoryVisible.includes('otherInfo') ? (
                            <CustomListingFields
                              publicData={publicData}
                              metadata={metadata}
                              listingFieldConfigs={listingConfig.listingFields}
                              categoryConfiguration={config.categoryConfiguration}
                              intl={intl}
                            />
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </ListingSection>
                ) : null}

                {listingType === DEAL_LISTING_TYPE ? (
                  <ListingSection
                    icon={<Calendar size={16} strokeWidth={2} />}
                    title={intl.formatMessage({ id: 'ListingPage.dealItemsHeading' })}
                  >
                    <div className={css.dealsItemWrapper}>
                      {listingType === DEAL_LISTING_TYPE
                        ? dealItemsListings?.map(dealItem => (
                            <DealItemsMaybe key={dealItem?.id?.uuid} dealItem={dealItem} />
                          ))
                        : null}
                    </div>
                  </ListingSection>
                ) : null}

                <ListingSection
                  icon={<MessageSquare size={16} strokeWidth={2} />}
                  title={intl.formatMessage({ id: 'ListingPage.sectionReviews' })}
                >
                  <ReviewSummary
                    listingPublicData={isDeal ? dealItemPublicData : publicData}
                    ratings={ratings}
                    ratingsInProgress={fetchRatingsInProgress}
                    ratingsError={fetchRatingsError}
                  />
                  <h4 className={css.reviewsSubheading}>
                    <FormattedMessage id="ReviewCards.titleListing" values={{ name: title }} />
                  </h4>
                  <ReviewCards
                    onManageDisableScrolling={onManageDisableScrolling}
                    reviews={reviews}
                    reviewsInProgress={fetchReviewsInProgress}
                    reviewsError={fetchReviewsError}
                  />
                </ListingSection>

                {!isBookingLink ? (
                  <ListingSection
                    icon={<MapPin size={16} strokeWidth={2} />}
                    title={intl.formatMessage({ id: 'ListingPage.sectionAuthor' })}
                  >
                    <SectionAuthorMaybe
                      title={title}
                      listing={businessProfileListing}
                      authorDisplayName={authorDisplayName}
                      onContactUser={onContactUser}
                      isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
                      onCloseInquiryModal={() => setInquiryModalOpen(false)}
                      sendInquiryError={sendInquiryError}
                      sendInquiryInProgress={sendInquiryInProgress}
                      onSubmitInquiry={onSubmitInquiry}
                      currentUser={currentUser}
                      onManageDisableScrolling={onManageDisableScrolling}
                      organizedCategories={organizedCategories}
                    />
                  </ListingSection>
                ) : null}

                {relatedItemsListings?.length > 0 ? (
                  <ListingSection
                    icon={<Tag size={16} strokeWidth={2} />}
                    title={
                      <span className={css.otherSectionTitleRow}>
                        <span>
                          {listingType === PRODUCT_LISTING_TYPE
                            ? intl.formatMessage({ id: 'ListingPage.otherProducts' })
                            : listingType === SERVICE_LISTING_TYPE
                            ? intl.formatMessage({ id: 'ListingPage.otherServices' })
                            : intl.formatMessage({ id: 'ListingPage.otherDeals' })}
                        </span>
                        <span
                          className={css.viewAll}
                          onClick={() => {
                            history.push(
                              createResourceLocatorString(
                                'AlgoliaSearchPage',
                                routeConfiguration,
                                { listingType: listingType + 's' },
                                { 'publicData.categoryLevel2': categoryLevel2 }
                              )
                            );
                          }}
                        >
                          <FormattedMessage id="ListingPage.viewAll" />
                        </span>
                      </span>
                    }
                  >
                    <div>
                      <div className={css.relatedListContainer}>
                        <Slider {...settings}>
                          {relatedItemsListings.map(item => {
                            const { listingType } = item?.attributes?.publicData || {};
                            const hit = listingToAlgoliaHit(item);
                            return listingType === DEAL_LISTING_TYPE ? (
                              <AlgoliaSearchDealCard
                                key={hit.objectID}
                                hit={hit}
                                currentUser={currentUser}
                                toggleFavorite={onToggleFavorite}
                                updatedDealCategories={updatedDealCategories}
                                history={history}
                                routeConfiguration={routeConfiguration}
                              />
                            ) : (
                              <AlgoliaSearchServiceCard
                                key={hit.objectID}
                                hit={hit}
                                currentUser={currentUser}
                                toggleFavorite={onToggleFavorite}
                                history={history}
                                routeConfiguration={routeConfiguration}
                              />
                            );
                          })}
                        </Slider>
                      </div>
                    </div>
                  </ListingSection>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([LISTING_PAGE_DRAFT_VARIANT, LISTING_PAGE_PENDING_APPROVAL_VARIANT]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingError,
    reviews,
    fetchReviewsInProgress,
    fetchReviewsError,
    ratings,
    fetchRatingsInProgress,
    fetchRatingsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    fetchStaffListingsError,
    fetchStaffListingsInProgress,
    currentPageStaffIds,
    businessProfileListing,
    currentPageDealItemIds,
    currentPageRelatedItemIds = [],
    itemMonthlyTimeSlots,
  } = state.ListingPage;

  const { currentUser } = state.user;

  const { categoriesSuccess } = state?.EditListingPage || {};

  const staffListings = getListingsById(state, currentPageStaffIds);
  const dealItemsListings = getListingsById(state, currentPageDealItemIds);
  const relatedItemsListings = getListingsById(state, currentPageRelatedItemIds);

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingError,
    reviews,
    fetchReviewsInProgress,
    fetchReviewsError,
    ratings,
    fetchRatingsInProgress,
    fetchRatingsError,
    monthlyTimeSlots,
    itemMonthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    staffListings,
    fetchStaffListingsError,
    fetchStaffListingsInProgress,
    businessProfileListing,
    dealItemsListings,
    categories: categoriesSuccess,
    relatedItemsListings,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params => dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message, attachments) =>
    dispatch(sendInquiry(listing, message, attachments)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onFetchStaffListings: params => dispatch(fetchStaffListings(params)),
  onToggleCart: (listingId, authorId, increment, extraParams) =>
    dispatch(toggleCart({ listingId, authorId, increment, extraParams })),
  onFetchDealItemMonthlyTimeSlots: listing => dispatch(fetchDealItemMonthlyTimeSlots(listing)),
  onFetchDealItemTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchDealItemTimeSlots(listingId, start, end, timeZone)),
  onToggleFavorite: (listingId, type, user) => dispatch(toggleFavorite(listingId, type, user)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedListingPage);

export default ListingPage;
